import { type FC, memo, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isBoolean, isNil, isObject, noop } from 'lodash';
// Import Types
import type { CJVisualizationArgumentValue, CJVisualizationDueDateUIModel } from 'types';
// Import Enums
import { CJVisualizationDueDateTypeEnum } from 'enums';
// Import UI Components
import { BusyLoader, Button, Dropdown, Tooltip } from '@geneui/components';
// Import Components
import CJCascadeDropdown from 'components/CustomerJourney/CJCascadeDropdown';
import { formulaToReferenceMapToUIModel } from 'components/CustomerJourney/config';
import CustomerJourneyVisualizationToggleSection from './CustomerJourneyVisualizationToggleSection';
// Import Helpers
import {
    getCJVisualizationDueDateFormConfig,
    getMatchedProperties,
    isCJReferenceInArgumentValue,
    mapPropertiesToCascadeDropdownModel,
} from 'services/customerJourney/helpers';
// Import Constants
import { l, CJArgumentValuesTypes, CJVisualizationDueDateTypeLabels } from 'constants/common';
// Import Styles
import styles from './customerJourneyVisualizationDueDate.module.scss';

interface Props {
    defaultValue?: Nilable<CJVisualizationDueDateUIModel>;
    node?: Nullable<AnyObject>;
    elements?: Nullable<AnyObject[]>;
    option?: Nullable<AnyObject>;
    collectedProperties?: AnyObject[];
    getUpdate?: (data: Partial<CJVisualizationDueDateUIModel>) => void;
}

const CustomerJourneyVisualizationDueDate: FC<Props> = (props) => {
    const {
        defaultValue = null,
        node = null,
        elements = null,
        option = null,
        collectedProperties = [],
        getUpdate = noop,
    } = props;

    const { t } = useTranslation();

    const [isEnabled, setIsEnabled] = useState<boolean>(defaultValue?.isEnabled === true);
    const [type, setType] = useState<Nullable<number>>(defaultValue?.type ?? null);
    const [value, setValue] = useState<Nullable<CJVisualizationArgumentValue>>(defaultValue?.value ?? null);
    const [isValueValid, setIsValueValid] = useState<Nullable<boolean>>(null);
    const [isReferenceActive, setIsReferenceActive] = useState<boolean>(
        isCJReferenceInArgumentValue(defaultValue?.value),
    );

    const formConfig = useMemo(() => getCJVisualizationDueDateFormConfig(type), [type]);
    const isReferenceOnly = formConfig?.isReferenceOnly === true;
    const isReferenceEditor = isReferenceOnly || isReferenceActive;

    const typesData = useMemo(
        () =>
            [CJVisualizationDueDateTypeEnum.DynamicDate, CJVisualizationDueDateTypeEnum.Duration].map((item) => ({
                value: item,
                label: t(CJVisualizationDueDateTypeLabels[item]),
            })),
        [t],
    );

    const formOption = useMemo(
        () => ({
            ...option,
            Name: option?.Name,
            BaseTypes: isNil(formConfig) ? [] : [formConfig.optionType],
            ModelRelationScope: option?.ModelRelationScope,
            Required: true,
        }),
        [option, formConfig],
    );

    const referenceProperties = useMemo(
        () =>
            isNil(formConfig)
                ? []
                : mapPropertiesToCascadeDropdownModel(getMatchedProperties(formConfig.optionType, collectedProperties)),
        [formConfig, collectedProperties],
    );

    const typeChangeHandler = (item) => {
        setType(item?.value ?? null);
        setValue(null);
        setIsValueValid(null);
        setIsReferenceActive(false);
    };

    const valueChangeHandler = (config, argumentInType = CJArgumentValuesTypes.StaticInArgument) => {
        return (data, paramArgumentInType = argumentInType) => {
            // forms report their validation state through the same callback
            if (!isNil(data?.isValid) && isBoolean(data.isValid)) {
                setIsValueValid(data.isValid);
                return;
            }

            let tmpData = data;
            if (paramArgumentInType === CJArgumentValuesTypes.ReferenceInArgument) {
                tmpData = { blockId: data?.parentValue, propertyName: data?.value, logicFunction: data?.logicFunction };
            }
            setValue(config.mapToApiModel(tmpData, paramArgumentInType, formOption));
        };
    };

    const referenceActiveClickHandler = () => {
        setValue(null);
        setIsValueValid(null);
        setIsReferenceActive(!isReferenceActive);
    };

    const referenceValidationChangeHandler = (isValid: boolean) => {
        setIsValueValid(isValid);
    };

    useEffect(() => {
        let isValid = true;
        if (isEnabled) {
            const hasValue = isReferenceEditor
                ? isObject(value) && !isNil((value as AnyObject).Block ?? (value as AnyObject).Arguments)
                : !isNil(value);
            const hasEditor = !isNil(formConfig) && !isNil(node);
            isValid = hasEditor && hasValue && isValueValid !== false;
        }
        getUpdate({ isEnabled, type, value, isValid });
        // DEPS: [getUpdate, formConfig] (formConfig is derived from type)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnabled, type, value, isValueValid, isReferenceActive, node]);

    const renderValueEditor = () => {
        if (isNil(formConfig) || isNil(node)) {
            return null;
        }
        const { Component } = formConfig;

        return (
            <div className={styles.dueDateValue}>
                {isReferenceEditor ? (
                    <CJCascadeDropdown
                        data={referenceProperties}
                        value={formulaToReferenceMapToUIModel(value)}
                        elements={elements ?? []}
                        onChange={valueChangeHandler(formConfig, CJArgumentValuesTypes.ReferenceInArgument)}
                        onValidationChange={referenceValidationChangeHandler}
                        option={formOption}
                        node={node}
                    />
                ) : (
                    // the CJ forms are React.lazy; a local boundary keeps the page from falling back to the route spinner
                    <Suspense fallback={<BusyLoader isBusy type="spinner" spinnerSize="medium" />}>
                        <Component
                            key={`${type}_${isReferenceActive}`}
                            getUpdate={valueChangeHandler(formConfig)}
                            option={formOption}
                            defaultValue={formConfig.mapToUIModel(value)}
                            elements={elements}
                            fromFilter={true}
                            data={referenceProperties}
                            node={node}
                        />
                    </Suspense>
                )}
                {!isReferenceOnly && (
                    <Tooltip text={t(l.CJBlockFilterReferenceDescription)}>
                        <Button
                            icon={isReferenceActive ? 'bc-icon-unlink' : 'bc-icon-link'}
                            onClick={referenceActiveClickHandler}
                            appearance="minimal"
                        >
                            {isReferenceActive ? t(l.PrimitiveValue) : t(l.Reference)}
                        </Button>
                    </Tooltip>
                )}
            </div>
        );
    };

    return (
        <CustomerJourneyVisualizationToggleSection
            label={t(l.DueDate)}
            description={t(l.DueDateDescription)}
            isChecked={isEnabled}
            onChange={setIsEnabled}
        >
            <div className={styles.dueDateFields}>
                <Dropdown
                    appearance="outline"
                    inputSize="default"
                    label={t(l.Type)}
                    labelAppearance="swap"
                    placeholder={t(l.Type)}
                    value={type}
                    data={typesData}
                    onChange={typeChangeHandler}
                    isValid={!isNil(type)}
                    errorText={t(l.CannotBeEmpty, { fieldName: t(l.Type) })}
                />
                {renderValueEditor()}
            </div>
        </CustomerJourneyVisualizationToggleSection>
    );
};

CustomerJourneyVisualizationDueDate.displayName = 'CustomerJourneyVisualizationDueDate';

export default memo(CustomerJourneyVisualizationDueDate);
